"use strict";

(() => {
  function getGap(track) {
    const styles = window.getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap) || 0;
  }

  function stopMediaIn(item) {
    if (!item) {
      return;
    }

    item.querySelectorAll("video").forEach((video) => {
      video.pause();

      try {
        video.currentTime = 0;
      } catch (error) {
        // Ignore browsers that block seeking while loading.
      }
    });

    item.querySelectorAll("iframe").forEach((iframe) => {
      const currentSrc = iframe.getAttribute("src");

      if (currentSrc && currentSrc !== "about:blank") {
        iframe.dataset.gallerySrc = currentSrc;
      }

      // Clearing src reliably stops YouTube/Vimeo playback.
      iframe.setAttribute("src", "about:blank");
    });
  }

  function prepareActiveMedia(item) {
    if (!item) {
      return;
    }

    item.querySelectorAll("iframe").forEach((iframe) => {
      const storedSrc = iframe.dataset.gallerySrc;
      const currentSrc = iframe.getAttribute("src");

      if (storedSrc && (!currentSrc || currentSrc === "about:blank")) {
        iframe.setAttribute("src", storedSrc);
      }
    });
  }

  function hideGalleryZoom(gallery) {
    if (!gallery) {
      return;
    }

    const lens = gallery.querySelector("[data-product-zoom-lens]");
    const result = gallery.querySelector("[data-product-zoom-result]");

    gallery.classList.remove("is-zooming");

    if (lens) {
      lens.hidden = true;
      lens.setAttribute("aria-hidden", "true");
    }

    if (result) {
      result.hidden = true;
      result.setAttribute("aria-hidden", "true");
      result.style.backgroundImage = "";
    }
  }

  function setActiveMedia(gallery, mediaId) {
    if (!gallery || mediaId === null || mediaId === undefined || mediaId === "") {
      return;
    }

    const mediaIdString = String(mediaId);
    const mediaItems = gallery.querySelectorAll("[data-product-media]");
    const thumbnails = gallery.querySelectorAll("[data-product-thumbnail]");

    hideGalleryZoom(gallery);

    // Stop previous media before switching the visible slide.
    mediaItems.forEach((item) => {
      if (String(item.dataset.mediaId) !== mediaIdString) {
        stopMediaIn(item);
      }
    });

    mediaItems.forEach((item) => {
      const isActive = String(item.dataset.mediaId) === mediaIdString;
      item.classList.toggle("is-active", isActive);

      if (isActive) {
        item.removeAttribute("hidden");
        prepareActiveMedia(item);
      } else {
        item.setAttribute("hidden", "");
      }
    });

    thumbnails.forEach((button) => {
      const isActive = String(button.dataset.mediaId) === mediaIdString;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-current", isActive ? "true" : "false");
    });

    const viewport = gallery.querySelector("[data-product-gallery-viewport]");
    const track = gallery.querySelector("[data-product-gallery-track]");

    if (viewport && track) {
      window.requestAnimationFrame(() => {
        scrollActiveThumbnailIntoView(viewport, track);
      });
    }
  }

  function isVerticalThumbsLayout() {
    return !window.matchMedia("(max-width: 767.98px)").matches;
  }

  function scrollActiveThumbnailIntoView(viewport, track) {
    const activeThumb = track.querySelector(".product-gallery__thumbnail.is-active");

    if (!activeThumb || !viewport) {
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();

    if (isVerticalThumbsLayout()) {
      if (thumbRect.top < viewportRect.top) {
        viewport.scrollBy({
          top: thumbRect.top - viewportRect.top - 8,
          behavior: "smooth",
        });
        return;
      }

      if (thumbRect.bottom > viewportRect.bottom) {
        viewport.scrollBy({
          top: thumbRect.bottom - viewportRect.bottom + 8,
          behavior: "smooth",
        });
      }

      return;
    }

    if (thumbRect.left < viewportRect.left) {
      viewport.scrollBy({
        left: thumbRect.left - viewportRect.left - 8,
        behavior: "smooth",
      });
      return;
    }

    if (thumbRect.right > viewportRect.right) {
      viewport.scrollBy({
        left: thumbRect.right - viewportRect.right + 8,
        behavior: "smooth",
      });
    }
  }

  function applyThumbnailWidths(viewport, track) {
    const thumbs = track.querySelectorAll(".product-gallery__thumbnail");

    if (!thumbs.length) {
      return;
    }

    if (isVerticalThumbsLayout()) {
      const thumbSize = viewport.clientWidth;

      if (!thumbSize) {
        return;
      }

      const thumbSizePx = `${thumbSize}px`;

      track.style.setProperty("--product-gallery-thumb-size", thumbSizePx);

      thumbs.forEach((thumb) => {
        thumb.style.flex = `0 0 ${thumbSizePx}`;
        thumb.style.width = "100%";
        thumb.style.height = thumbSizePx;
        thumb.style.maxWidth = "100%";
      });

      return;
    }

    const viewportWidth = viewport.clientWidth;

    if (!viewportWidth) {
      return;
    }

    const gap = getGap(track);
    let visibleCount = 5;

    if (window.matchMedia("(max-width: 767.98px)").matches) {
      visibleCount = 4;
    }

    if (window.matchMedia("(max-width: 479.98px)").matches) {
      visibleCount = 3;
    }

    const thumbSize = (viewportWidth - gap * (visibleCount - 1)) / visibleCount;
    const thumbSizePx = `${thumbSize}px`;

    track.style.setProperty("--product-gallery-thumb-size", thumbSizePx);

    thumbs.forEach((thumb) => {
      thumb.style.flex = `0 0 ${thumbSizePx}`;
      thumb.style.width = thumbSizePx;
      thumb.style.height = thumbSizePx;
      thumb.style.maxWidth = thumbSizePx;
    });
  }

  function initMainImageSwipe(gallery) {
    const mainArea = gallery.querySelector("[data-product-gallery-main]");
    const track = gallery.querySelector("[data-product-gallery-track]");

    if (!mainArea || !track) {
      return;
    }

    const getThumbnails = () => track.querySelectorAll("[data-product-thumbnail]");

    let touchStartX = 0;
    let touchStartY = 0;
    let ignoreSwipe = false;

    mainArea.addEventListener(
      "touchstart",
      (event) => {
        if (!event.touches.length) {
          return;
        }

        ignoreSwipe = Boolean(
          event.target.closest("video, iframe, .product-gallery__video, .product-gallery__external-video")
        );

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      },
      { passive: true }
    );

    mainArea.addEventListener(
      "touchend",
      (event) => {
        if (ignoreSwipe || !event.changedTouches.length) {
          return;
        }

        const touchEndX = event.changedTouches[0].clientX;
        const touchEndY = event.changedTouches[0].clientY;
        const deltaX = touchEndX - touchStartX;
        const deltaY = touchEndY - touchStartY;
        const swipeThreshold = 50;

        if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }

        const thumbnails = Array.from(getThumbnails());

        if (thumbnails.length <= 1) {
          return;
        }

        const activeIndex = thumbnails.findIndex((thumbnail) =>
          thumbnail.classList.contains("is-active")
        );

        if (activeIndex === -1) {
          return;
        }

        const nextIndex =
          deltaX < 0
            ? Math.min(activeIndex + 1, thumbnails.length - 1)
            : Math.max(activeIndex - 1, 0);

        if (nextIndex === activeIndex) {
          return;
        }

        thumbnails[nextIndex].click();
      },
      { passive: true }
    );
  }

  function getRenderedImageRect(img, containerRect) {
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    if (!naturalWidth || !naturalHeight) {
      return null;
    }

    const containerRatio = containerRect.width / containerRect.height;
    const imageRatio = naturalWidth / naturalHeight;

    let width;
    let height;

    if (imageRatio > containerRatio) {
      width = containerRect.width;
      height = containerRect.width / imageRatio;
    } else {
      height = containerRect.height;
      width = containerRect.height * imageRatio;
    }

    return {
      width,
      height,
      left: containerRect.left + (containerRect.width - width) / 2,
      top: containerRect.top + (containerRect.height - height) / 2,
    };
  }

  function initImageZoom(gallery) {
    const mainArea = gallery.querySelector("[data-product-gallery-main]");
    const lens = gallery.querySelector("[data-product-zoom-lens]");
    const result = gallery.querySelector("[data-product-zoom-result]");

    if (!mainArea || !lens || !result || gallery.dataset.galleryZoomInitialized === "true") {
      return;
    }

    const hoverMedia = window.matchMedia("(hover: hover) and (pointer: fine)");
    const desktopMedia = window.matchMedia("(min-width: 992px)");
    let frame = null;

    function canZoom() {
      return hoverMedia.matches && desktopMedia.matches;
    }

    function hideZoom() {
      gallery.classList.remove("is-zooming");
      gallery.closest(".product-main__gallery")?.classList.remove("is-zooming");
      gallery.closest(".product-main")?.classList.remove("is-zooming");
      lens.hidden = true;
      result.hidden = true;
      lens.setAttribute("aria-hidden", "true");
      result.setAttribute("aria-hidden", "true");
      result.style.backgroundImage = "";
    }

    function getResultRect(containerRect) {
      const edge = 16;
      const gap = 18;
      const isRtl = document.documentElement.dir === "rtl";
      const left = isRtl ? edge : containerRect.right + gap;
      const right = isRtl ? containerRect.left - gap : window.innerWidth - edge;
      const width = right - left;
      const top = Math.max(edge, containerRect.top);
      const height = Math.min(
        containerRect.height,
        window.innerHeight - top - edge
      );

      if (width < 320 || height < 300) {
        return null;
      }

      return { left, top, width, height };
    }

    function getActiveZoomTarget() {
      const activeMedia = gallery.querySelector("[data-product-media].is-active");

      if (!activeMedia || activeMedia.dataset.mediaType !== "image") {
        return null;
      }

      const zoomSrc = activeMedia.dataset.zoomSrc;
      const image = activeMedia.querySelector(".product-gallery__main-image");

      if (!zoomSrc || !image) {
        return null;
      }

      return { activeMedia, zoomSrc, image };
    }

    function updateZoom(clientX, clientY) {
      const target = getActiveZoomTarget();

      if (!target || !canZoom()) {
        hideZoom();
        return;
      }

      const containerRect = mainArea.getBoundingClientRect();
      const imageRect = getRenderedImageRect(target.image, containerRect);
      const resultRect = getResultRect(containerRect);

      if (!imageRect || !resultRect) {
        hideZoom();
        return;
      }

      if (
        clientX < imageRect.left ||
        clientX > imageRect.left + imageRect.width ||
        clientY < imageRect.top ||
        clientY > imageRect.top + imageRect.height
      ) {
        hideZoom();
        return;
      }

      const resultAspect = resultRect.width / resultRect.height;
      let lensWidth = imageRect.width * 0.42;
      let lensHeight = lensWidth / resultAspect;
      const maxLensHeight = imageRect.height * 0.52;

      if (lensHeight > maxLensHeight) {
        lensHeight = maxLensHeight;
        lensWidth = lensHeight * resultAspect;
      }

      lensWidth = Math.min(lensWidth, imageRect.width);
      lensHeight = Math.min(lensHeight, imageRect.height);

      const halfLensWidth = lensWidth / 2;
      const halfLensHeight = lensHeight / 2;

      let lensLeft = clientX - imageRect.left - halfLensWidth;
      let lensTop = clientY - imageRect.top - halfLensHeight;

      lensLeft = Math.max(0, Math.min(lensLeft, imageRect.width - lensWidth));
      lensTop = Math.max(0, Math.min(lensTop, imageRect.height - lensHeight));

      const offsetLeft = imageRect.left - containerRect.left;
      const offsetTop = imageRect.top - containerRect.top;

      lens.style.width = `${lensWidth}px`;
      lens.style.height = `${lensHeight}px`;
      lens.style.transform = `translate(${offsetLeft + lensLeft}px, ${offsetTop + lensTop}px)`;

      const zoomRatio = resultRect.width / lensWidth;

      result.style.left = `${resultRect.left}px`;
      result.style.top = `${resultRect.top}px`;
      result.style.width = `${resultRect.width}px`;
      result.style.height = `${resultRect.height}px`;
      result.style.backgroundImage = `url("${target.zoomSrc}")`;
      result.style.backgroundSize = `${imageRect.width * zoomRatio}px ${imageRect.height * zoomRatio}px`;
      result.style.backgroundPosition = `-${lensLeft * zoomRatio}px -${lensTop * zoomRatio}px`;

      gallery.classList.add("is-zooming");
      gallery.closest(".product-main__gallery")?.classList.add("is-zooming");
      gallery.closest(".product-main")?.classList.add("is-zooming");
      lens.hidden = false;
      result.hidden = false;
      lens.setAttribute("aria-hidden", "false");
      result.setAttribute("aria-hidden", "false");
    }

    function onPointerMove(event) {
      if (!canZoom()) {
        return;
      }

      const clientX = event.clientX;
      const clientY = event.clientY;

      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      frame = window.requestAnimationFrame(() => {
        updateZoom(clientX, clientY);
      });
    }

    mainArea.addEventListener("pointerenter", onPointerMove);
    mainArea.addEventListener("pointermove", onPointerMove);
    mainArea.addEventListener("pointerleave", hideZoom);
    mainArea.addEventListener("pointercancel", hideZoom);

    hoverMedia.addEventListener("change", hideZoom);
    desktopMedia.addEventListener("change", hideZoom);

    gallery.dataset.galleryZoomInitialized = "true";
  }

  function syncVerticalThumbsHeight(gallery) {
    const mainArea = gallery.querySelector("[data-product-gallery-main]");
    const thumbs = gallery.querySelector("[data-product-gallery-thumbs]");

    if (!mainArea || !thumbs) {
      return;
    }

    if (!isVerticalThumbsLayout()) {
      thumbs.style.height = "";
      thumbs.style.maxHeight = "";
      return;
    }

    const height = Math.round(mainArea.getBoundingClientRect().height);

    if (height > 0) {
      thumbs.style.height = `${height}px`;
      thumbs.style.maxHeight = `${height}px`;
    }
  }

  function initProductGallerySlider(gallery) {
    if (gallery.dataset.gallerySliderInitialized === "true") {
      return;
    }

    const viewport = gallery.querySelector("[data-product-gallery-viewport]");
    const track = gallery.querySelector("[data-product-gallery-track]");
    const mainArea = gallery.querySelector("[data-product-gallery-main]");

    gallery.addEventListener("click", (event) => {
      const thumbnail = event.target.closest("[data-product-thumbnail]");

      if (!thumbnail || !gallery.contains(thumbnail)) {
        return;
      }

      setActiveMedia(gallery, thumbnail.dataset.mediaId);
    });

    if (viewport && track) {
      let resizeFrame = null;

      const syncLayout = () => {
        syncVerticalThumbsHeight(gallery);
        applyThumbnailWidths(viewport, track);
        scrollActiveThumbnailIntoView(viewport, track);
      };

      window.addEventListener("resize", () => {
        if (resizeFrame) {
          window.cancelAnimationFrame(resizeFrame);
        }

        resizeFrame = window.requestAnimationFrame(syncLayout);
      });

      if (typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(() => {
          syncLayout();
        });

        resizeObserver.observe(viewport);

        if (mainArea) {
          resizeObserver.observe(mainArea);
        }
      }

      window.addEventListener("load", syncLayout, { once: true });
      syncLayout();
    }

    initMainImageSwipe(gallery);
    initImageZoom(gallery);

    gallery.dataset.gallerySliderInitialized = "true";
  }

  function initProductGallerySliders(scope = document) {
    scope.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      initProductGallerySlider(gallery);
    });
  }

  window.productGalleryUi = {
    setActiveMedia,
    init: initProductGallerySliders,
  };

  document.addEventListener("DOMContentLoaded", () => {
    initProductGallerySliders();
  });

  document.addEventListener("shopify:section:load", (event) => {
    initProductGallerySliders(event.target);
  });
})();
